// src/components/TagsManager.tsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, IconButton, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Toolbar, TextField, ToggleButton, ToggleButtonGroup, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, MenuItem, Select, FormControl,
  InputLabel, FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

import type { Tag, TagCategory, TagRegistry } from '../types/position.ts';
import type { DefaultStyles, ScopeFilter, TagRow } from '../utils/tags.ts';
import {
  buildTagRows, canAddChild, dashArrayFor, flattenTagTree, parentProblem, resolveStyleDetail,
  selectionCategoryId, topMostTags, MAX_TAG_DEPTH,
} from '../utils/tags.ts';
import {
  bulkTagImpact, bulkDeleteTags, bulkReparentTags, tagCategoryImpact, deleteTagCategory,
} from '../utils/tagsApi.ts';
import type { BulkTagImpact } from '../utils/tagsApi.ts';
import { useTagsRegistry } from './TagsProvider.tsx';
import MarkerShape from './MarkerShape.tsx';
import TagCategoryDialog from './TagCategoryDialog.tsx';
import TagEditor, { parentReasons } from './TagEditor.tsx';

interface TagsManagerProps {
  isAdmin: boolean;
  scopeUserId: number | null;
  defaults: DefaultStyles;
}

interface EditorTarget {
  editing: Tag | null;
  categoryId: number | null;
  parentId: number | null;
}

interface CategoryImpact {
  tagCount: number;
  assignmentCount: number;
  shadowingUserCount: number | null;
}

const CHECK_COLUMN_WIDTH = 48;
const CHECK_CELL_SX = {
  '&.MuiTableCell-paddingCheckbox': {
    width: CHECK_COLUMN_WIDTH,
    minWidth: CHECK_COLUMN_WIDTH,
    maxWidth: CHECK_COLUMN_WIDTH,
  },
};
const COLUMN_COUNT = 8;

const DEPTH_MESSAGE = `Adding a child here would exceed the maximum tag depth of ${MAX_TAG_DEPTH}.`;
const MOVE_MESSAGE = 'Select one or more tags from the same category to move them.';
const NO_WRITABLE_CATEGORY = 'No writable category is available.';

const isWritableCategory = (cat: TagCategory): boolean =>
  !cat.readonly && !(cat.scope === 'global' && cat.shadowed_by_me);

const canEditCategory = (cat: TagCategory, isAdmin: boolean): boolean =>
  isWritableCategory(cat) && (cat.scope === 'user' || isAdmin);

const scopeLabel = (cat: TagCategory): string => (cat.scope === 'user' ? 'Personal' : 'Global');

const compareCategories = (a: TagCategory, b: TagCategory): number => {
  if (a.scope !== b.scope) return a.scope === 'user' ? -1 : 1;
  return a.tag_category.localeCompare(b.tag_category);
};

const ExpandButton: React.FC<{ open: boolean; visible: boolean; onClick: () => void }> = ({ open, visible, onClick }) =>
  visible ? (
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(); }} aria-label={open ? 'Collapse' : 'Expand'}>
      {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </IconButton>
  ) : (
    <Box sx={{ width: 34, display: 'inline-block' }} />
  );

const TagsManager: React.FC<TagsManagerProps> = ({ isAdmin, scopeUserId, defaults }) => {
  const { registry, refresh } = useTagsRegistry();

  const [categoryText, setCategoryText] = useState('');
  const [tagText, setTagText] = useState('');
  const [scope, setScope] = useState<ScopeFilter>('both');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [selected, setSelected] = useState<number[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [categoryEditor, setCategoryEditor] = useState<{ editing: TagCategory | null } | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [tagDelete, setTagDelete] = useState<{ ids: number[]; impact: BulkTagImpact } | null>(null);
  const [categoryDelete, setCategoryDelete] = useState<{ category: TagCategory; impact: CategoryImpact } | null>(null);

  const rows = useMemo(
    () => buildTagRows(registry, { categoryText, tagText, scope }, expanded),
    [registry, categoryText, tagText, scope, expanded],
  );

  const categories = useMemo(() => Array.from(registry.categories.values()).sort(compareCategories), [registry]);
  const writableCategories = useMemo(() => categories.filter(isWritableCategory), [categories]);
  const userCategories = categories.filter(c => c.scope === 'user');
  const globalCategories = categories.filter(c => c.scope === 'global');
  const availableGlobalsForShadow = globalCategories.filter(g => !userCategories.some(u => u.shadows_category_id === g.id));

  const selectedCategoryId = selectionCategoryId(selected, registry);
  const movingTags = useMemo(() => topMostTags(selected, registry), [selected, registry]);

  const reload = async () => {
    try {
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelected = (id: number) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const changeCategoryText = (value: string) => { setSelected([]); setCategoryText(value); };
  const changeTagText = (value: string) => { setSelected([]); setTagText(value); };
  const changeScope = (value: ScopeFilter) => { setSelected([]); setScope(value); };

  const openNewTag = (categoryId: number | null, parentId: number | null) => {
    if (writableCategories.length === 0) {
      setNotice(NO_WRITABLE_CATEGORY);
      return;
    }
    setEditor({ editing: null, categoryId, parentId });
  };

  const openEditTag = (tag: Tag) => {
    setEditor({ editing: tag, categoryId: tag.category_id, parentId: tag.parent_tag_id });
  };

  const addChild = (tag: Tag) => {
    if (!canAddChild(tag, registry)) {
      setNotice(DEPTH_MESSAGE);
      return;
    }
    openNewTag(tag.category_id, tag.id);
  };

  const requestTagDelete = async (ids: number[]) => {
    try {
      const impact = await bulkTagImpact(ids, scopeUserId);
      setTagDelete({ ids, impact });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const confirmTagDelete = async () => {
    if (!tagDelete) return;
    try {
      await bulkDeleteTags(tagDelete.ids, scopeUserId);
      setTagDelete(null);
      setSelected([]);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const requestCategoryDelete = async (category: TagCategory) => {
    try {
      const impact = await tagCategoryImpact(category.id);
      setCategoryDelete({ category, impact });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const confirmCategoryDelete = async () => {
    if (!categoryDelete) return;
    try {
      await deleteTagCategory(categoryDelete.category.id, scopeUserId);
      setCategoryDelete(null);
      setSelected([]);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const requestMove = () => {
    if (selectedCategoryId == null) {
      setNotice(MOVE_MESSAGE);
      return;
    }
    setMoveOpen(true);
  };

  const renderRow = (row: TagRow) => {
    if (row.kind === 'empty') {
      return (
        <TableRow key={row.key}>
          <TableCell colSpan={COLUMN_COUNT}>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>No tags in this category.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (row.kind === 'category') {
      const cat = row.category;
      const writable = isWritableCategory(cat);
      return (
        <TableRow key={row.key} hover sx={{ opacity: row.dimmed ? 0.5 : 1, backgroundColor: 'action.hover' }}>
          <TableCell padding="checkbox" sx={CHECK_CELL_SX} />
          <TableCell>
            <Stack direction="row" alignItems="center">
              <ExpandButton open={row.expanded} visible onClick={() => toggleExpanded(row.key)} />
              <Typography variant="subtitle2">{cat.tag_category}</Typography>
            </Stack>
          </TableCell>
          <TableCell />
          <TableCell>
            <Typography variant="body2">
              {scopeLabel(cat)}{cat.readonly ? ' (Read Only)' : ''}
            </Typography>
          </TableCell>
          <TableCell>
            {cat.scope === 'user' && row.shadowedName && (
              <Typography variant="body2" color="text.secondary">{row.shadowedName}</Typography>
            )}
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell align="right">
            {writable && (
              <IconButton size="small" title="Add Tag" onClick={() => openNewTag(cat.id, null)}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            {canEditCategory(cat, isAdmin) && (
              <IconButton size="small" title="Edit Category" onClick={() => setCategoryEditor({ editing: cat })}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {writable && (
              <IconButton size="small" title="Delete Category" onClick={() => requestCategoryDelete(cat)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
      );
    }

    const tag = row.tag;
    const writable = isWritableCategory(row.category);
    const style = resolveStyleDetail(tag, registry, defaults);
    const pathOwn = style.origins.path.color === 'own';
    const markerOwn = style.origins.marker.fillColor === 'own';
    const markerType = style.marker.type ?? 'circle';
    return (
      <TableRow key={row.key} hover selected={selected.includes(tag.id)} sx={{ opacity: row.dimmed ? 0.5 : 1 }}>
        <TableCell padding="checkbox" sx={CHECK_CELL_SX}>
          {writable && (
            <Checkbox size="small" checked={selected.includes(tag.id)} onChange={() => toggleSelected(tag.id)} />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" sx={{ pl: row.depth * 3 }}>
            <ExpandButton open={row.expanded} visible={row.hasChildren} onClick={() => toggleExpanded(row.key)} />
            <Typography variant="body2">{tag.tag}</Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">{tag.sort_order}</TableCell>
        <TableCell>
          <Typography variant="body2">{scopeLabel(row.category)}</Typography>
        </TableCell>
        <TableCell />
        <TableCell>
          {style.path.type === 'none' ? (
            <Typography variant="caption" color="text.secondary">None</Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: pathOwn ? 1 : 0.5 }}>
              <svg width={36} height={8} aria-hidden="true">
                <line
                  x1={0} y1={4} x2={36} y2={4}
                  stroke={style.path.color ?? '#000000'}
                  strokeOpacity={style.path.opacity ?? 1}
                  strokeWidth={3}
                  strokeDasharray={dashArrayFor(style.path.type, style.path.dash)}
                />
              </svg>
              <Typography variant="caption">{style.path.color}</Typography>
            </Stack>
          )}
        </TableCell>
        <TableCell>
          {markerType === 'none' ? (
            <Typography variant="caption" color="text.secondary">None</Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: markerOwn ? 1 : 0.5 }}>
              <MarkerShape
                type={markerType}
                size={12}
                fill={style.marker.fillColor ?? '#000000'}
                fillOpacity={style.marker.fillType === 'none' ? 0 : 1}
                stroke={style.marker.borderColor ?? '#000000'}
                strokeWidth={style.marker.borderType === 'none' ? 0 : 1}
                strokeOpacity={1}
              />
              <Typography variant="caption">{style.marker.fillColor}</Typography>
            </Stack>
          )}
        </TableCell>
        <TableCell align="right">
          {writable && (
            <>
              <IconButton size="small" title="Add Child Tag" onClick={() => addChild(tag)}
                sx={{ opacity: canAddChild(tag, registry) ? 1 : 0.4 }}>
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Edit Tag" onClick={() => openEditTag(tag)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Delete Tag" onClick={() => requestTagDelete([tag.id])}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{
      height: '100vh',
      width: 'calc(100vw - 56px)',
      position: 'absolute',
      zIndex: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField label="Category" size="small" value={categoryText}
              onChange={(e) => changeCategoryText(e.target.value)} />
            <TextField label="Tag" size="small" value={tagText}
              onChange={(e) => changeTagText(e.target.value)} />
            <ToggleButtonGroup size="small" exclusive value={scope}
              onChange={(_, value: ScopeFilter | null) => { if (value) changeScope(value); }}>
              <ToggleButton value="own">Personal</ToggleButton>
              <ToggleButton value="global">Global</ToggleButton>
              <ToggleButton value="both">Both</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {selected.length > 0 && (
              <>
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => requestTagDelete(selected)}>
                  Delete
                </Button>
                <Button startIcon={<DriveFileMoveIcon />} onClick={requestMove}
                  sx={{ opacity: selectedCategoryId == null ? 0.5 : 1 }}>
                  Move
                </Button>
              </>
            )}
            <Button startIcon={<AddIcon />} onClick={() => setCategoryEditor({ editing: null })}>
              New Category
            </Button>
            <Button startIcon={<AddIcon />} variant="contained"
              onClick={() => openNewTag(writableCategories[0]?.id ?? null, null)}>
              New Tag
            </Button>
          </Stack>
        </Toolbar>

        <Snackbar
          open={notice !== null || error !== null}
          autoHideDuration={error === null ? 4000 : null}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setNotice(null);
            setError(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {error !== null ? (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          ) : (
            <Alert severity="warning" onClose={() => setNotice(null)}>{notice}</Alert>
          )}
        </Snackbar>

        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={CHECK_CELL_SX} />
                <TableCell>Name</TableCell>
                <TableCell align="right">Sort</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Shadows Global</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Marker</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(renderRow)}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TagCategoryDialog
        open={categoryEditor !== null}
        onClose={() => setCategoryEditor(null)}
        editing={categoryEditor?.editing ?? null}
        isAdmin={isAdmin}
        scopeUserId={scopeUserId}
        availableGlobalsForShadow={availableGlobalsForShadow}
        onSaved={async () => { setCategoryEditor(null); await reload(); }}
        onError={setError}
      />

      {editor && (
        <TagEditor
          registry={registry}
          writableCategories={writableCategories}
          defaults={defaults}
          scopeUserId={scopeUserId}
          editing={editor.editing}
          initialCategoryId={editor.categoryId}
          initialParentId={editor.parentId}
          onClose={() => setEditor(null)}
          onSaved={async () => { setEditor(null); await reload(); }}
          onError={setError}
        />
      )}

      {moveOpen && selectedCategoryId != null && (
        <MoveTagsDialog
          categoryId={selectedCategoryId}
          ids={selected}
          moving={movingTags}
          registry={registry}
          scopeUserId={scopeUserId}
          onClose={() => setMoveOpen(false)}
          onMoved={async () => { setMoveOpen(false); setSelected([]); await reload(); }}
          onError={setError}
        />
      )}

      <Dialog open={tagDelete !== null} onClose={() => setTagDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Tags</DialogTitle>
        <DialogContent>
          {tagDelete && (
            <Typography variant="body2">
              This deletes {tagDelete.impact.subtreeTagCount} tag(s) including their children and removes{' '}
              {tagDelete.impact.assignmentCount} position assignment(s).
              {tagDelete.ids.length > 1 && tagDelete.impact.topLevel.length > 0 &&
                ` Top-level: ${tagDelete.impact.topLevel.map(t => t.tag).join(', ')}.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmTagDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDelete !== null} onClose={() => setCategoryDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          {categoryDelete && (
            <Typography variant="body2">
              Deleting "{categoryDelete.category.tag_category}" removes {categoryDelete.impact.tagCount} tag(s) and{' '}
              {categoryDelete.impact.assignmentCount} position assignment(s).
              {categoryDelete.category.scope === 'global' && categoryDelete.impact.shadowingUserCount != null && (
                ` Users shadowing it: ${categoryDelete.impact.shadowingUserCount}.`
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmCategoryDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MoveTagsDialog: React.FC<{
  categoryId: number;
  ids: number[];
  moving: Tag[];
  registry: TagRegistry;
  scopeUserId: number | null;
  onClose: () => void;
  onMoved: () => Promise<void>;
  onError: (msg: string) => void;
}> = ({ categoryId, ids, moving, registry, scopeUserId, onClose, onMoved, onError }) => {
  const [parentId, setParentId] = useState<number | null>(null);

  const categoryTags = useMemo(
    () => Array.from(registry.tags.values()).filter(t => t.category_id === categoryId),
    [registry, categoryId],
  );
  const options = useMemo(() => flattenTagTree(categoryTags), [categoryTags]);
  const reasons = useMemo(() => parentReasons(categoryTags, moving, registry), [categoryTags, moving, registry]);

  const parentTag = parentId == null ? null : registry.tags.get(parentId) ?? null;
  const problem = parentProblem(parentTag, moving, registry);
  const rootProblem = parentProblem(null, moving, registry);

  const save = async () => {
    if (problem) return;
    try {
      await bulkReparentTags(ids, parentId, scopeUserId);
      await onMoved();
    } catch (err) { onError((err as Error).message); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Move {ids.length} Selected Tag(s)</DialogTitle>
      <DialogContent>
        <FormControl fullWidth error={problem !== null} sx={{ mt: 1 }}>
          <InputLabel>New Parent</InputLabel>
          <Select
            label="New Parent"
            value={parentId == null ? 'root' : String(parentId)}
            onChange={(e) => setParentId(e.target.value === 'root' ? null : Number(e.target.value))}
          >
            <MenuItem value="root" disabled={rootProblem !== null}>(Root)</MenuItem>
            {options.map(({ tag, depth }) => (
              <MenuItem
                key={tag.id}
                value={String(tag.id)}
                disabled={parentProblem(tag, moving, registry) !== null}
                sx={{ pl: 2 + depth * 2 }}
              >
                {tag.tag}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{reasons.join(' ') || ' '}</FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={problem !== null}>Move</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagsManager;
